import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { useHubStore } from "@/components/hub/use-hub-store";
import { getActivityConfig } from "@/lib/activities/registry";
import { useActivityFlagEnabled } from "@/lib/activities/use-activity-flag-enabled";
import { useActivity } from "@/lib/api/activities";
import { useConversationParticipants } from "@/lib/hooks/conversations/use-conversation-participants";
import { parseMessageData } from "@/lib/utils/parse-message-data";
import { useCallback, useMemo } from "react";

export interface ActivitySendbirdData {
  entity_id: string;
  activity_type: string;
  status: string;
  mentorship_id?: string;
  result?: Record<string, unknown>;
}

interface ActivityMessageOptions {
  context?: ChatRenderContext;
}

export function useActivityMessage(
  message: { data?: string },
  options?: ActivityMessageOptions,
) {
  const activityData = useMemo(
    () => parseMessageData<ActivitySendbirdData>(message.data),
    [message.data],
  );

  const entityId = activityData?.entity_id ?? "";
  const {
    data: activity,
    isError,
    isPending,
  } = useActivity(entityId, {
    retryOnMount: false,
    staleTime: Infinity,
  });

  const activityModule = getActivityConfig(activityData?.activity_type ?? "");
  const isFlagEnabled = useActivityFlagEnabled(activityModule?.featureFlag) || import.meta.env.DEV;
  const isEnabled = Boolean(activityModule) && isFlagEnabled;

  const { mentor, teen } = useConversationParticipants(options?.context);

  const open = useHubStore((state) => state.open);
  const navigateToView = useHubStore((state) => state.navigateToView);

  const openInHub = useCallback(() => {
    if (!entityId) return;
    const config = getActivityConfig(activityData?.activity_type ?? "");
    if (!config) return;
    open();
    navigateToView(config.hubView, entityId);
  }, [entityId, activityData?.activity_type, open, navigateToView]);

  return {
    activityData,
    activity,
    isError,
    isPending,
    entityId,
    isEnabled,
    teenName: teen.name ?? "Teen",
    mentorName: mentor.name ?? undefined,
    mentorAvatarUrl: mentor.avatarUrl,
    teenAvatarUrl: teen.avatarUrl,
    openInHub,
  };
}
